'use client'

import { createContext, useContext, type ComponentType, type ReactNode } from 'react'

// Shared-area surface seam (25_SHARED_DESKUI_SKELETON.md §5/§6.2): the surface
// supplies its loading widget (a look-only Layer C component by design) and
// its visual class tokens for the shared structural pieces.

export interface MsgDeskLoadingProps {
  contract?: string
  variant?: 'page' | 'panel' | 'inline' | 'button' | 'overlay'
  label?: string
  className?: string
}

export interface SharedSkin {
  navItem: string
  navItemSelected: string
  navLabel: string
  navLoading: string
  moreLink: string
  testBadge: string
  testBadgeInfo: string
  dialogBackdrop: string
  dialogWindow: string
  savingBackdrop: string
  savingContent: string
  msgBoxOverlay: string
  msgBoxSpacer: string
  msgBox: string
  msgBoxInfo: string
  msgBoxText: string
  msgBoxPin: string
  msgBoxCaptchaImg: string
  msgBoxButtons: string
  msgBoxClose: string
  msgBoxHiddenCancel: string
  qrOverlay: string
  /** QR-IN-PLACE: optional panel look for the MAIN-msg composer (near the
   * menu Reply control). When absent, the main composer falls back to the
   * qrOverlay dialog. */
  qrMainPanel?: string
  /** QR-IN-PLACE: optional wrapper look for the inline ROW composer the row
   * host mounts under the replied row. */
  qrInline?: string
  qrRoot: string
  qrForm: string
  qrEditForm: string
  qrTextarea: string
  qrEmailLabel: string
  qrCheckbox: string
  qrEmailLink: string
  qrEmailValue: string
  qrActions: string
  qrSaveButton: string
  qrShadowRight: string
  qrShadowBottom: string
  pinOverlay: string
  pinBox: string
  pinMessage: string
  pinInput: string
  pinActions: string
  pinButton: string
  pinButtonPrimary: string
}

export interface SharedSurface {
  Loading: ComponentType<MsgDeskLoadingProps>
  skin: SharedSkin
}

const SharedSurfaceContext = createContext<SharedSurface | null>(null)

export function SharedSurfaceProvider({ value, children }: { value: SharedSurface; children: ReactNode }) {
  return <SharedSurfaceContext.Provider value={value}>{children}</SharedSurfaceContext.Provider>
}

export function useSharedSurface(): SharedSurface {
  const surface = useContext(SharedSurfaceContext)
  if (!surface) {
    throw new Error('useSharedSurface: no SharedSurfaceProvider is mounted above this shared skeleton element')
  }
  return surface
}

/** Null-tolerant variant for skeleton elements that may render in trees
 * without a shared-surface provider (e.g. the row host). */
export function useSharedSurfaceOptional(): SharedSurface | null {
  return useContext(SharedSurfaceContext)
}
